/**
 * Pillar SEO pages: short public URLs + extended copy (merged into SERVICE_LANDING_CONTENT).
 * Helpers are local to avoid circular imports with serviceLandingContent.js
 */
import { buildExpandedManpowerServices, buildExpandedHousekeepingServices } from './landingSeoExpandedOfferings';
import { SITE_BRAND_NAME } from '../constants/siteBranding';

const SITE_NAME = SITE_BRAND_NAME;

const bx = (id, heading, paragraphs = [], listItems = []) => ({
  id,
  heading,
  paragraphs,
  listItems: listItems || []
});

const fq = (question, answer) => ({ question, answer });

/** Map clean URLs (no /service prefix) → content slug */
export const PILLAR_PATH_TO_SLUG = {
  '/valet-parking-ahmedabad': 'valet-parking-ahmedabad',
  '/facility-management-ahmedabad': 'facility-management-ahmedabad',
  '/manpower-services-ahmedabad': 'manpower-services-ahmedabad',
  '/office-boy-services-ahmedabad': 'office-boy-services-ahmedabad',
  '/peon-services-ahmedabad': 'peon-services-ahmedabad',
  '/labour-supply-ahmedabad': 'labour-ahmedabad',
  '/housekeeping-services-ahmedabad': 'housekeeping-services-ahmedabad',
  '/cleaning-services-ahmedabad': 'cleaning-services-ahmedabad'
};

export const PILLAR_EXTENDED_CONTENT = {
  'facility-management-ahmedabad': {
    slug: 'facility-management-ahmedabad',
    publicUrlPath: '/facility-management-ahmedabad',
    name: 'Facility Management',
    metaTitle:
      'Facility Management in Ahmedabad | Facility Management Services in Ahmedabad | Khodal Housekeeping Agency | Integrated Facility Management (IFM) Gujarat',
    metaDescription:
      'Facility management in Ahmedabad: staffing, housekeeping, valet & soft services for offices, malls & industries. Facility management services in Ahmedabad: housekeeping, staffing, valet & soft services for offices, malls, hospitals & societies. Khodal Housekeeping Agency. Call +91 9265472938. Integrated facility management (IFM) & workplace soft services across SG Highway, Satellite, Bodakdev, Thaltej, Bopal, Vastrapur, Naroda & Gujarat.',
    metaKeywords:
      'facility management services in ahmedabad, facility management services ahmedabad, facility management ahmedabad, facility management company ahmedabad, soft services ahmedabad, integrated facility services gujarat, IFM ahmedabad, integrated facility management ahmedabad, building facility services ahmedabad, workplace services ahmedabad, SG Highway facility management, facility vendor ahmedabad',
    h1: 'Facility Management Services in Ahmedabad',
    intro: `${SITE_NAME} is a facility management company in Ahmedabad that helps businesses run cleaner, safer, and more organised workplaces. From housekeeping and janitorial support to valet parking, office boys, peons, and contract manpower, we combine people, processes, and supervision so your facility teams stay accountable. Whether you operate on SG Highway, in Bodakdev, Thaltej, Satellite, or industrial belts around Ahmedabad, our local supervisors understand traffic patterns, society norms, and corporate expectations that are unique to Gujarat’s commercial capital.`,
    breadcrumbName: 'Facility Management Ahmedabad',
    sections: [
      bx(
        'what-is',
        'What facility management means in Ahmedabad',
        [
          'Facility management goes beyond “hiring a few sweepers.” It is the coordinated delivery of soft services—cleaning, support staff, parking, pest control partners, and periodic deep cleaning—that keeps your building presentable and compliant. In Ahmedabad, rapid construction and mixed-use developments mean tenants expect spotless lobbies, reliable lift areas, and responsive support teams. We help facility managers and admin heads translate those expectations into rosters, attendance sheets, and replacement plans that actually work on the ground.',
          'Because Ahmedabad hosts corporate headquarters, hospitals, educational institutes, and retail hubs within a compact geography, even a single absent staff member can bottleneck an entire shift. That is why we emphasise backup pools, documented handovers, and WhatsApp-friendly escalation so your team is never guessing whom to call at 8 a.m. on a Monday.'
        ],
        []
      ),
      bx(
        'who-we-serve',
        'Who we serve across Ahmedabad and Gujarat',
        [],
        [
          'Corporate towers and business parks on SG Highway & Prahlad Nagar',
          'Hospitals, clinics, and diagnostic centres',
          'Retail malls, showrooms, and hospitality venues',
          'Residential societies and premium apartments',
          'Factories, warehouses, and logistics yards needing bundled soft services'
        ]
      ),
      bx(
        'how-we-work',
        'How we implement facility programmes',
        [
          'Every engagement starts with a walkthrough: we capture footfall, peak hours, statutory requirements, and existing pain points. Next, we propose staffing mixes—housekeeping, office support, valet, or hybrid models—and agree on KPIs such as response time for complaints or turnaround for replacements. Once mobilised, our field coordinators track attendance, uniform compliance, and consumables usage so you receive predictable invoices instead of surprise add-ons.',
          'We also coordinate with your security, IT, and admin teams for access cards, locker allocations, and induction briefings. This collaborative approach reduces friction when new attendants join your Ahmedabad campus.'
        ],
        []
      ),
      bx(
        'why-local',
        'Why local expertise matters in Ahmedabad',
        [
          'Ahmedabad’s climate, festival calendars, and traffic flows influence how services should be scheduled—think monsoon lobby care, Navratri event surges, or year-end corporate parties that need simultaneous valet and housekeeping surge staff. A vendor that only works remotely from another city rarely anticipates these rhythms. Our teams live and hire locally, which means faster replacements and culturally aware staff who communicate comfortably with Gujarati and Hindi-speaking tenants.',
          'We also understand municipal and residential association nuances, helping committees document vendor credentials and insurance copies without slowing down onboarding.'
        ],
        []
      ),
      bx(
        'areas',
        'Areas we routinely support in and around Ahmedabad',
        [
          'From corporate pockets on SG Highway and Prahlad Nagar to hospitals near Ellisbridge, from retail on CG Road to new townships toward Shilaj and South Bopal, facility expectations differ by micro-market. High-rise offices may need night pantry support; hospitals need infection-control discipline; societies need committee-friendly reporting. We tune shift overlap, language mix, and supervisor visit cadence to match each micro-market instead of applying a one-size-fits-all roster.',
          'If you are expanding to Gandhinagar, Sanand, or Changodar industrial belts, we can mirror your Ahmedabad SOPs so brand experience stays consistent while respecting longer commute realities for staff.'
        ],
        []
      ),
      bx(
        'cta',
        'Talk to a facility specialist today',
        [
          'Ready to consolidate facility vendors? Call +91 9265472938, WhatsApp us, or submit the inquiry form for a structured proposal tailored to your Ahmedabad location. We will share timelines, sample rosters, and commercial options within one business day whenever possible.'
        ],
        []
      )
    ],
    faqs: [
      fq(
        'What is included under facility management in Ahmedabad?',
        'Typical scopes include housekeeping, janitorial support, washroom hygiene, consumables planning, office boy or peon coverage, valet parking for select sites, and coordination with specialised partners such as pest control. We customise the bundle based on your building type and footfall.'
      ),
      fq('Do you manage only offices or also residential societies?', `${SITE_NAME} supports corporate offices, retail, healthcare, educational institutes, and large residential complexes across Ahmedabad. Society MCs can rely on us for common-area cleaning and support staffing.`),
      fq('How do you handle replacements if someone resigns?', 'We maintain bench strength for critical roles and follow a documented replacement protocol so your facility coverage in Ahmedabad resumes quickly, usually within agreed SLA windows.'),
      fq('Can we start with a pilot floor before full-building rollout?', 'Yes. Many clients pilot one tower or one floor to benchmark quality, then expand. Pilots help fine-tune shift timings and equipment lists before scaling city-wide.'),
      fq('Do you provide valet along with housekeeping?', 'Absolutely. Bundling valet parking with housekeeping is common for malls, hotels, and premium offices in Ahmedabad. Mention both requirements when you call +91 9265472938 so we design one contract.')
    ]
  },

  'manpower-services-ahmedabad': buildExpandedManpowerServices(bx, fq, SITE_NAME),

  'housekeeping-services-ahmedabad': buildExpandedHousekeepingServices(bx, fq, SITE_NAME),

  'cleaning-services-ahmedabad': {
    slug: 'cleaning-services-ahmedabad',
    publicUrlPath: '/cleaning-services-ahmedabad',
    name: 'Cleaning Services',
    metaTitle:
      'Cleaning Services in Ahmedabad | Offices & Retail | Commercial Cleaning Services in Ahmedabad | Khodal Housekeeping Agency',
    metaDescription:
      'Cleaning services in Ahmedabad for offices, retail & industries. Daily janitorial, deep clean & event support. Khodal Housekeeping Agency. Call +91 9265472938. Commercial cleaning company Ahmedabad: office janitorial, society, mall & post-construction cleaning across SG Highway, Satellite, Bopal, Prahlad Nagar, Naroda & Gujarat.',
    metaKeywords:
      'cleaning services ahmedabad, commercial cleaning ahmedabad, office cleaning company, deep cleaning ahmedabad, cleaning services in ahmedabad, commercial cleaning services ahmedabad, office cleaning services ahmedabad, janitorial company ahmedabad, society cleaning ahmedabad, mall cleaning ahmedabad, deep cleaning services ahmedabad, housekeeping and cleaning ahmedabad',
    h1: 'Cleaning Services in Ahmedabad',
    intro: `${SITE_NAME} offers cleaning services in Ahmedabad that span daily janitorial care, periodic deep cleaning, post-construction sparkle cleaning, and event turnaround crews. Ahmedabad’s commercial corridors generate high dust loads; retail storefronts need streak-free glass; IT parks need anti-static routines. We combine mechanised equipment where feasible with skilled manual detailing where machines cannot reach, ensuring your premises stay client-ready.`,
    breadcrumbName: 'Cleaning Services Ahmedabad',
    sections: [
      bx(
        'daily',
        'Daily and periodic cleaning programmes',
        [
          'Daily programmes focus on lobbies, washrooms, cabins, pantries, and waste segregation points. Periodic programmes add scrubbing, chair vacuuming, façade touch-ups, and kitchen degreasing. We schedule periodic bursts during low-occupancy windows so productivity in your Ahmedabad office is not disrupted.',
          'Retail clients often need night shifts; we align staffing accordingly and provide reflective uniforms for safety in parking zones.'
        ],
        []
      ),
      bx(
        'deep',
        'Deep cleaning and speciality scopes',
        [
          'Deep cleaning tackles grout, ceiling vents, ledges, and neglected corners. We also coordinate carpet shampooing, chair shampooing, and high-rise glass partners when your scope demands specialists. Post-renovation cleaning removes paint specks, labels, and fine dust before handover photos.',
          'Event cleaning includes pre-function polish and post-event trash sweeps so venues flip quickly for the next booking—common for Ahmedabad’s wedding and conference season.'
        ],
        []
      ),
      bx(
        'safety',
        'Safety, chemicals, and documentation',
        [
          'MSDS sheets, dilution charts, and PPE kits are standard for our supervisors. We train teams on wet-floor signage, electrical safety near water, and lift lobby cord discipline. Insurance and indemnity documents can be shared with your procurement team for vendor registration.',
          'If your Ahmedabad facility requires vendor gate passes or police verification badges, we guide new joiners through the paperwork early.'
        ],
        []
      ),
      bx(
        'reporting',
        'Reporting that finance and admin teams appreciate',
        [
          'Digital attendance logs, consumable usage summaries, and photographic evidence (where approved) help facility heads justify budgets. We can align formats with your CA or ISO audit needs.'
        ],
        []
      ),
      bx(
        'industrial',
        'Industrial and logistics sites in Ahmedabad region',
        [
          'Warehouses near Sanand, Changodar, and Naroda need different cleaning rhythms than corporate towers—more tyre dust, oil spots, and metal swarf. We combine blower cycles, wet-dry protocols, and PPE discipline so shop-floor safety is not compromised while offices on the same campus stay dust-controlled.',
          'If you operate 24x7 lines, we align handover windows with production pauses and security walkthroughs so cleaning never becomes an afterthought bolted onto maintenance tickets.'
        ],
        []
      ),
      bx(
        'cta',
        'Request a customised cleaning plan',
        [
          'Send floor plans or slot preferences on WhatsApp or call +91 9265472938. We will schedule a site visit and share a clear scope document before commercials are discussed.'
        ],
        []
      )
    ],
    faqs: [
      fq('Do you provide one-time deep cleaning?', 'Yes. One-time, quarterly, or annual deep cleaning packages are available for offices, retail, and homes in Ahmedabad.'),
      fq('Can you work on Sundays?', 'We can schedule Sunday or night shifts depending on your access permissions and security protocols.'),
      fq('Do you bring your own equipment?', 'Standard kits include mops, buckets, microfibres, and safe chemicals. Heavy machines can be arranged for larger sites.'),
      fq('How do you price retail stores in malls?', 'Pricing considers store size, glass frontage, mall rules, and shift windows. We factor Ahmedabad mall loading bay constraints into mobilisation plans.'),
      fq('What if we need emergency cleaning after a leak?', 'Call +91 9265472938—we prioritise water-damage mitigation requests and deploy rapid-response teams when capacity allows.')
    ]
  },

  'office-boy-services-ahmedabad': {
    slug: 'office-boy-services-ahmedabad',
    publicUrlPath: '/office-boy-services-ahmedabad',
    name: 'Office Boy Services',
    metaTitle:
      'Office Boy Services in Ahmedabad | Khodal Housekeeping Agency | Office Boy in Ahmedabad | Office Attendant & Pantry Support',
    metaDescription:
      'Office boy services in Ahmedabad: courier, pantry & desk support for corporates. Verified staff from Khodal Housekeeping Agency. Call +91 9265472938. Office boy agency Ahmedabad: attendant, mailroom, pantry & front-desk support for offices on SG Highway, Satellite, Bodakdev, Prahlad Nagar, Thaltej & Vastrapur.',
    metaKeywords:
      'office boy services ahmedabad, office boy agency ahmedabad, office helper ahmedabad, corporate office support, office boy in ahmedabad, office boy ahmedabad, office attendant ahmedabad, pantry boy ahmedabad, mailroom staff ahmedabad, corporate support staff gujarat',
    h1: 'Office Boy Services in Ahmedabad',
    intro: `${SITE_NAME} provides office boy services in Ahmedabad for organisations that need reliable support for courier, pantry, meeting rooms, and ad-hoc tasks. Office boys are often the first impression for visitors and the glue for internal teams—when they are absent, courier piles up and meetings start late. We recruit for punctuality, grooming, and basic digital literacy so your Ahmedabad office maintains professionalism without micromanaging daily chores.`,
    breadcrumbName: 'Office Boy Services Ahmedabad',
    sections: [
      bx(
        'tasks',
        'Typical responsibilities',
        [],
        [
          'Courier dispatch, tracking, and acknowledgement logs',
          'Tea, coffee, and pantry restocking',
          'Photocopying, spiral binding, and document movement',
          'Meeting room setup—water, markers, projector checks',
          'Escorting visitors within security protocols'
        ]
      ),
      bx(
        'fit',
        'Finding the right fit for your culture',
        [
          'Some Ahmedabad corporates prefer multilingual staff; others prioritise experience with high-volume couriers. We capture these nuances during intake calls and share candidate summaries before deployment.',
          'We also align break timings with your cafeteria vendor and security frisking norms so new joiners integrate smoothly on day one.'
        ],
        []
      ),
      bx(
        'governance',
        'Governance and escalation',
        [
          'Attendance dashboards, uniform audits, and surprise lobby checks keep standards high. If a skill gap emerges—say, basic Excel for dispatch logs—we arrange refresher coaching instead of blind replacements.',
          'Escalations route through a single coordinator who knows your site history, avoiding generic call-centre loops.'
        ],
        []
      ),
      bx(
        'scale',
        'Scaling across Ahmedabad locations',
        [
          'If you open satellite branches in Gandhinagar or Sanand, we can mirror SOPs and rotate trained office boys for consistency. Centralised billing keeps finance teams happy.'
        ],
        []
      ),
      bx(
        'coordination',
        'Coordination with admin, IT, and security',
        [
          'Great office boys anticipate bottlenecks: printer jams before board meetings, water jars before town halls, courier cut-offs for Ahmedabad hubs. We encourage shadow days with outgoing staff so muscle memory transfers. Security frisking, biometric enrolment, and parking pass renewals are handled proactively so day-one productivity is high.',
          'For hybrid workplaces with hot desks, we align pantry rounds with occupancy sensors or simple floor-walk patterns so support stays visible without hovering.'
        ],
        []
      ),
      bx(
        'contact',
        'Hire office boys with confidence',
        [
          'Dial +91 9265472938 or WhatsApp your roster needs. We will revert with profiles, commercials, and onboarding checklists tailored to your Ahmedabad office.'
        ],
        []
      )
    ],
    faqs: [
      fq('What is the difference between office boy and peon?', 'In practice many Ahmedabad offices use the terms interchangeably. We clarify task lists during discovery so profiles match your expectations, whether courier-heavy or pantry-heavy.'),
      fq('Can office boys handle confidential documents?', 'We train staff on confidentiality basics and recommend sealed pouches or logbooks for sensitive files. Ultimate document policy remains with your organisation.'),
      fq('Do you provide female office attendants?', 'Yes, when job roles and workplace policies permit, we can propose suitable profiles.'),
      fq('What uniforms do you provide?', 'Standardised uniforms with ID badges reinforce brand consistency; customisation is possible for large campuses.'),
      fq('How do replacements work during vacations?', 'Plan leave calendars with us in advance so backup attendants shadow primary staff before handovers.')
    ]
  },

  'peon-services-ahmedabad': {
    slug: 'peon-services-ahmedabad',
    publicUrlPath: '/peon-services-ahmedabad',
    name: 'Peon Services',
    metaTitle:
      'Peon Services in Ahmedabad | Office & Court Support | Peon in Ahmedabad | Bank & Branch Errands | Khodal Housekeeping Agency',
    metaDescription:
      'Peon services in Ahmedabad for offices, banks & institutions. Courier, files & errands by Khodal Housekeeping Agency. Call +91 9265472938. Peon agency Ahmedabad: file movement, court & registry errands, bank branch support across Ellisbridge, Ashram Road, Navrangpura, SG Highway & Gujarat.',
    metaKeywords:
      'peon services ahmedabad, office peon ahmedabad, peon agency ahmedabad, peon for bank ahmedabad, peon in ahmedabad, peon ahmedabad, office peon services ahmedabad, bank peon ahmedabad, file movement staff ahmedabad, institution peon gujarat',
    h1: 'Peon Services in Ahmedabad',
    intro: `${SITE_NAME} supplies peon services in Ahmedabad for banks, government liaison offices, legal chambers, educational institutes, and corporates that depend on file movement, branch-to-branch errands, and disciplined queue management. Peons often navigate crowded markets, registry offices, and courier hubs—so reliability and integrity matter as much as speed. Our hiring filters emphasise reference checks, literacy for registers, and road sense for two-wheeler errands when permitted.`,
    breadcrumbName: 'Peon Services Ahmedabad',
    sections: [
      bx(
        'role',
        'Modern peon roles in Ahmedabad offices',
        [
          'Beyond stereotypical “chai” duties, peons today coordinate inter-department files, track challans, assist with archives, and support front desks during peak visitor hours. We document each responsibility so performance reviews are objective.',
          'For institutions near courts or government offices, we factor travel time and parking constraints into SLA expectations so your team is not unfairly penalised for Ahmedabad traffic.'
        ],
        []
      ),
      bx(
        'vetting',
        'Vetting and compliance',
        [
          'We collect ID proofs, local references, and prior employment letters where available. Orientation covers respectful communication, data privacy, and safe riding habits for two-wheeler tasks.',
          'Clients may request additional police verification—we coordinate appointments without delaying mobilisation unnecessarily.'
        ],
        []
      ),
      bx(
        'continuity',
        'Continuity planning',
        [
          'Cross-training peons on basic office boy tasks reduces disruption during leave. Digital task trackers (where clients permit phones) help supervisors monitor pending errands transparently.'
        ],
        []
      ),
      bx(
        'institutions',
        'Peons for banks, schools, and liaison offices',
        [
          'Educational institutes in Ahmedabad often need peons for labs, libraries, and exam logistics; banks need disciplined staff for branch-to-branch file shuttles with zero tolerance for delays. We align hiring to your institution’s tone—formal uniforms for banks, softer palettes for schools—while keeping safety training universal.',
          'When government or court-related errands spike seasonally, we pre-agree surge rates or backup riders so your branch manager is not negotiating in the middle of a quarter-close week.'
        ],
        []
      ),
      bx(
        'cta',
        'Strengthen your file-movement team',
        [
          'Call +91 9265472938 or WhatsApp branch locations and errand patterns. We will propose staffing, shift overlaps, and commercial options tuned to Ahmedabad realities.'
        ],
        []
      )
    ],
    faqs: [
      fq('Can peons handle bank branch errands?', 'Yes, we regularly support financial institutions with inter-branch file movement subject to your internal compliance approvals.'),
      fq('Do peons work half-day?', 'Half-day, full-day, and split-shift models are possible depending on workload and security access.'),
      fq('What if confidential files are misplaced?', 'We train staff on chain-of-custody basics and recommend sealed bags plus acknowledgement logs. Incidents are escalated immediately to your admin head.'),
      fq('Are two-wheeler errands insured?', 'Commercial terms clarify liability for authorised errands. Clients should share insurance or indemnity requirements upfront.'),
      fq('Can we hire peons for multiple branches?', 'Yes. Multi-branch rostering is supported with location codes in attendance reports.')
    ]
  }
};
